import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import GiftAnimation from "@/components/GiftAnimation";

/**
 * GiftPicker — opens a TikTok-style gift drawer on Track pages, Artist
 * profiles, and comments. Renders a 7-gift grid with prices, plays the
 * appropriate animation when the gift sends, and never disturbs the audio
 * player (we don't unmount any sibling components on send).
 *
 * Props:
 *   targetType  : "track" | "artist" | "comment"
 *   targetId    : submission_id | user_id | comment_id
 *   recipientId : (optional) artist user_id — used for "you can't send to
 *                  yourself" guard before a network call.
 *   currentUserId: (optional) viewer's own user_id — same guard.
 *   onSent      : (optional) callback fired after a successful send.
 *   compact     : (optional) renders a small icon button instead of full text.
 */
const GIFT_FALLBACK = [
  { id: "fire",     emoji: "🔥", name: "Fire",       coins: 10,    tier: "small" },
  { id: "mic",      emoji: "🎤", name: "Mic",        coins: 50,    tier: "small" },
  { id: "icy_heart",emoji: "💙", name: "Icy Heart",  coins: 100,   tier: "medium" },
  { id: "barz",     emoji: "✍🏽", name: "Barz",       coins: 250,   tier: "medium" },
  { id: "trophy",   emoji: "🏆", name: "Trophy",     coins: 1000,  tier: "large" },
  { id: "goat",     emoji: "🐐", name: "GOAT",       coins: 4000,  tier: "large" },
  { id: "diamond",  emoji: "💎", name: "Diamond",    coins: 8000,  tier: "large" },
];

const GiftPicker = ({
  targetType,
  targetId,
  recipientId,
  currentUserId,
  currentUserRole,
  onSent,
  compact = false,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState(GIFT_FALLBACK);
  const [wallet, setWallet] = useState(null);
  const [sending, setSending] = useState(false);
  const [animation, setAnimation] = useState(null);
  // Phase 7.1 Milestone D Part 1 — Personal Supporter Card
  const [meRank, setMeRank] = useState(null);
  // Phase 7.1 Milestone E — Daily Gift Streak
  const [streak, setStreak] = useState(null);
  const isAdminViewer = currentUserRole === "admin";

  useEffect(() => {
    let cancelled = false;
    api.get("/gifts/catalog").then((r) => {
      if (!cancelled && r.data?.gifts?.length) setCatalog(r.data.gifts);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const refreshWallet = async () => {
    try {
      const r = await api.get("/wallet/me");
      setWallet(r.data?.wallet);
    } catch (_e) {
      // Not logged in — keep wallet null so we can show "Sign in to gift"
      setWallet(null);
    }
  };

  // Reuses /api/top-gifters/{kind}/{id}/me. We only fetch when the
  // drawer opens (not on every render) AND we re-fetch after a send so
  // the rank/badge nudge updates instantly. No-op for "comment" targets
  // since they don't have their own supporter ranking.
  const refreshMyRank = async () => {
    if (targetType === "comment") {
      setMeRank(null);
      return;
    }
    if (!currentUserId) {
      setMeRank(null);
      return;
    }
    try {
      const url = targetType === "track"
        ? `/top-gifters/track/${targetId}/me`
        : `/top-gifters/artist/${targetId}/me`;
      const r = await api.get(url);
      setMeRank(r.data || null);
    } catch (_e) {
      setMeRank(null);
    }
  };

  const refreshStreak = async () => {
    if (!currentUserId) {
      setStreak(null);
      return;
    }
    try {
      const r = await api.get("/gifts/streak/me");
      setStreak(r.data || null);
    } catch (_e) {
      setStreak(null);
    }
  };

  useEffect(() => {
    if (open) {
      refreshWallet();
      refreshMyRank();
      refreshStreak();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isSelf = recipientId && currentUserId && recipientId === currentUserId;

  const grantTestCoins = async () => {
    try {
      const r = await api.post("/wallet/grant-test-coins", {});
      setWallet(r.data?.wallet);
      toast.success(`+${r.data?.granted || 0} test coins added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to grant test coins");
    }
  };

  const send = async (gift) => {
    if (sending) return;
    if (isSelf) {
      toast.info("You can't send gifts to yourself");
      return;
    }
    if (wallet && (wallet.balance ?? 0) < gift.coins) {
      toast.error("Not enough coins — tap “Buy Coins” to top up your wallet");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/gifts/send", {
        gift_id: gift.id,
        target_type: targetType,
        target_id: targetId,
      });
      // Trigger the animation. The overlay is fixed-position and lives
      // OUTSIDE the AudioPlayer subtree, so playback is never interrupted.
      setAnimation({
        gift,
        tier: r.data?.animation_tier || gift.tier,
        timestamp: Date.now(),
      });
      setWallet(r.data?.wallet);
      toast.success(`${gift.emoji} ${gift.name} sent!`);
      // 🎯 Streak: surface the new state in the picker AND celebrate any
      // newly-unlocked milestone (badge + bonus coins) with a toast.
      const s = r.data?.streak;
      if (s) {
        setStreak({
          streak_days: s.streak_days,
          longest_streak_days: s.longest_streak_days,
          last_gift_date: s.last_gift_date,
          is_active_today: true,
          is_alive: true,
          badges: s.badges || [],
          next_milestone_at: null,
        });
        if (s.newly_unlocked_badge) {
          toast.success(
            `${s.newly_unlocked_badge.emoji} ${s.newly_unlocked_badge.label} unlocked!` +
            (s.bonus_credited ? ` +${s.bonus_credited} bonus coins 🪙` : "")
          );
        } else if (s.bonus_credited) {
          toast.success(`🎯 Streak +${s.bonus_credited} bonus coins!`);
        }
      }
      // Refresh the personal supporter rank so the card updates
      // immediately to reflect the new total / rank / next-rank gap.
      refreshMyRank();
      if (onSent) onSent(r.data);
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to send gift";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          compact
            ? `inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-pink-500/15 hover:bg-pink-500/25 border border-pink-500/40 text-pink-200 text-xs font-semibold transition-colors ${className}`
            : `inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white text-sm font-bold transition-all shadow-md hover:shadow-pink-500/40 ${className}`
        }
        data-testid={`gift-picker-open-${targetType}-${targetId}`}
      >
        <span>🎁</span>
        <span>{compact ? "Gift" : "Send Gift"}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="gift-picker-overlay"
        >
          <div
            className="bg-gradient-to-b from-[#1a0d2e] to-[#0a0a14] border border-pink-500/30 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="gift-picker-panel"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white text-lg font-bold">Send a Gift 🎁</h3>
                <p className="text-pink-200 text-xs">
                  Show love — gifts boost the artist's earnings & track ranking
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-white text-xl"
                aria-label="Close"
                data-testid="gift-picker-close"
              >
                ×
              </button>
            </div>

            {/* 🎯 Phase 7.1 Milestone E — Daily Gift Streak chip. Shown
                whenever the viewer has any streak history (alive or
                broken) so they can see what they're playing for. */}
            {wallet && streak && (streak.streak_days > 0 || streak.longest_streak_days > 0) && (
              <div
                className="flex items-center justify-between gap-2 px-3 py-2 mb-3 rounded-lg bg-gradient-to-r from-orange-500/15 via-amber-500/15 to-rose-500/10 border border-orange-500/40"
                data-testid="gift-picker-streak"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xl leading-none">
                      {streak.is_alive ? "🔥" : "💤"}
                    </span>
                    <span className="text-white font-bold text-sm">
                      {streak.is_alive
                        ? `${streak.streak_days}-Day Streak`
                        : "Streak broken — gift today to start again"}
                    </span>
                  </div>
                  {streak.is_alive && streak.next_milestone_at && (
                    <p className="text-[11px] text-orange-200/90 mt-0.5">
                      +{streak.next_milestone_at.days_remaining} day
                      {streak.next_milestone_at.days_remaining === 1 ? "" : "s"} to{" "}
                      <span className="font-semibold text-white">
                        {streak.next_milestone_at.badge?.emoji}{" "}
                        {streak.next_milestone_at.badge?.label}
                      </span>
                    </p>
                  )}
                </div>
                {Array.isArray(streak.badges) && streak.badges.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-end max-w-[55%]">
                    {streak.badges.slice(0, 3).map((b) => (
                      <span
                        key={b.id}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold text-white/90"
                        title={`Unlocked at ${b.threshold} days`}
                        data-testid={`gift-picker-streak-badge-${b.id}`}
                      >
                        <span>{b.emoji}</span>
                        <span>{b.label}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Wallet line */}
            <div
              className="flex items-center justify-between gap-3 px-3 py-2 mb-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
              data-testid="gift-picker-wallet"
            >              {wallet ? (
                <>
                  <div className="text-amber-200 text-sm">
                    <span className="font-bold text-base">{(wallet.balance || 0).toLocaleString()}</span>{" "}
                    <span className="text-amber-300/70 text-xs">coins</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/wallet"
                      className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white font-bold shadow"
                      data-testid="gift-picker-buy-coins"
                    >
                      Buy Coins
                    </Link>
                    {isAdminViewer && (
                      <button
                        type="button"
                        onClick={grantTestCoins}
                        className="text-xs px-2.5 py-1 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-400/40"
                        data-testid="gift-picker-grant-test-coins"
                        title="Admin QA: grant test coins"
                      >
                        + Test Coins
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <Link to="/login" className="text-amber-200 text-sm w-full text-center">
                  Sign in to gift
                </Link>
              )}
            </div>

            {/* Phase 7.1 Milestone D Part 1 — Personal Supporter Card.
                Shown for returning supporters (rank > 0). Surfaces rank,
                their cumulative coins on this target, earned badges, and
                an optional "+X to overtake #N-1" nudge when within reach.
                Hidden if the viewer hasn't gifted here yet. */}
            {wallet && targetType !== "comment" && meRank?.rank && (
              <div
                className="flex items-start gap-3 mb-3 px-3 py-2.5 rounded-lg bg-gradient-to-r from-amber-500/15 via-pink-500/15 to-purple-500/15 border border-amber-500/40"
                data-testid="gift-picker-personal-card"
              >
                <span className="text-2xl leading-none flex-shrink-0" aria-hidden>
                  {meRank.rank === 1 ? "🥇" : meRank.rank === 2 ? "🥈" : meRank.rank === 3 ? "🥉" : "🏅"}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold leading-tight">
                    You're <span className="text-amber-200">#{meRank.rank}</span>
                    {targetType === "track" ? " on this track" : " for this artist"}
                    <span className="text-pink-200/70"> · </span>
                    <span className="text-amber-100">{meRank.total_coins.toLocaleString()}c gifted</span>
                  </p>
                  {Array.isArray(meRank.supporter_badges) && meRank.supporter_badges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {meRank.supporter_badges.map((b) => (
                        <span
                          key={b.id}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] font-semibold text-white/90"
                          title={b.label}
                          data-testid={`gift-picker-personal-badge-${b.id}`}
                        >
                          <span>{b.emoji}</span>
                          <span>{b.label}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {meRank.next_rank_within_reach && meRank.coins_to_next_rank && (
                    <p
                      className="text-[11px] text-pink-200/90 mt-1"
                      data-testid="gift-picker-next-rank-nudge"
                    >
                      ⚡ <span className="font-semibold text-pink-100">+{meRank.coins_to_next_rank.toLocaleString()}c</span>{" "}
                      to overtake <span className="text-amber-200">#{meRank.rank - 1}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {catalog.map((g) => {
                const canAfford = wallet && (wallet.balance ?? 0) >= g.coins;
                return (
                  <button
                    key={g.id}
                    type="button"
                    disabled={sending || isSelf || !wallet}
                    onClick={() => send(g)}
                    className={`group relative flex flex-col items-center justify-center gap-1 p-3 rounded-xl border transition-all ${
                      canAfford
                        ? "bg-gradient-to-br from-pink-500/15 to-purple-500/15 border-pink-500/40 hover:border-pink-400 hover:scale-105"
                        : "bg-gray-800/60 border-gray-700 opacity-60"
                    } disabled:cursor-not-allowed`}
                    data-testid={`gift-pick-${g.id}`}
                  >
                    <span className="text-3xl group-hover:scale-110 transition-transform">
                      {g.emoji}
                    </span>
                    <span className="text-white text-xs font-semibold truncate max-w-full">
                      {g.name}
                    </span>
                    <span className="text-amber-300 text-[10px] font-bold">
                      {g.coins.toLocaleString()} coins
                    </span>
                    {g.tier === "large" && (
                      <span className="absolute top-1 right-1 text-[8px] uppercase tracking-wider px-1 rounded bg-pink-500/30 text-pink-100 border border-pink-500/40">
                        Big
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {isSelf && (
              <p className="mt-3 text-center text-xs text-gray-500">
                You can't send gifts to yourself.
              </p>
            )}
          </div>
        </div>
      )}

      {animation && (
        <GiftAnimation
          gift={animation.gift}
          tier={animation.tier}
          onDone={() => setAnimation(null)}
          key={animation.timestamp}
        />
      )}
    </>
  );
};

export default GiftPicker;
